import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";

/**
 * /book?token=… — a family chooses a time for a start-of-year 1-on-1.
 *
 * No sign-in, on purpose. Only 8 of 30 families have ever signed in to
 * Scholar, so a page behind login would reach almost nobody. The token in the
 * email is the credential; the edge function does all the checking.
 *
 * Talks to book-meeting-slot with a plain fetch rather than
 * supabase.functions.invoke, because invoke discards the response body on any
 * non-2xx — and the 409s here are exactly the answers a family needs to read:
 * "someone just took that time", "that is less than 24 hours away".
 *
 * Times are shown in Pacific, labelled as such, because that is when Dr. Li is
 * available. A family in another time zone also sees their own local time.
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL ?? "";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY ?? "";
const TZ = "America/Los_Angeles";

interface Slot {
  slot_id: string;
  starts_at: string;
  duration_min: number;
}
interface Booking {
  slot_id: string;
  starts_at: string;
  duration_min: number;
  location: string | null;
  can_change: boolean;
}
interface LoadResult {
  ok: true;
  student_first: string;
  timezone: string;
  booking: Booking | null;
  slots: Slot[];
}

async function call(
  body: Record<string, unknown>,
): Promise<{ ok: boolean; data: any }> {
  try {
    const res = await fetch(`${SUPABASE_URL}/functions/v1/book-meeting-slot`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok && data?.ok !== false, data };
  } catch {
    return {
      ok: false,
      data: {
        error:
          "We could not reach the server. Check your connection and try again.",
      },
    };
  }
}

const dayFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: TZ,
  weekday: "long",
  month: "long",
  day: "numeric",
});
const timeFmt = new Intl.DateTimeFormat("en-US", {
  timeZone: TZ,
  hour: "numeric",
  minute: "2-digit",
});
const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

function localHint(iso: string): string | null {
  if (!localTz || localTz === TZ) return null;
  const t = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(new Date(iso));
  return `${t} where you are`;
}

export default function BookMeetingPage() {
  const token = useMemo(
    () => new URLSearchParams(window.location.search).get("token") ?? "",
    [],
  );

  const [state, setState] = useState<LoadResult | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [justDone, setJustDone] = useState<"booked" | "moved" | null>(null);

  const load = useCallback(async () => {
    if (!token) {
      setLoadErr(
        "This link is missing its code. Please use the button in your email.",
      );
      return;
    }
    const { ok, data } = await call({ action: "load", token });
    if (!ok) {
      setLoadErr(data?.error ?? "This link is not valid.");
      return;
    }
    setState(data as LoadResult);
    setPicking(!data.booking);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const byDay = useMemo(() => {
    const groups: { day: string; slots: Slot[] }[] = [];
    for (const s of state?.slots ?? []) {
      const d = dayFmt.format(new Date(s.starts_at));
      const g = groups.find((x) => x.day === d);
      if (g) g.slots.push(s);
      else groups.push({ day: d, slots: [s] });
    }
    return groups;
  }, [state]);

  async function confirm() {
    if (!chosen) return;
    setSaving(true);
    setSaveErr(null);
    const { ok, data } = await call({ action: "book", token, slot_id: chosen });
    setSaving(false);
    if (!ok) {
      setSaveErr(data?.error ?? "We could not save that. Please try again.");
      /* A lost race or a time that just crossed the 24-hour line: refresh the
         list so the family is not left staring at a time that is gone. */
      if (data?.code === "taken" || data?.code === "too_late") {
        setChosen(null);
        await load();
      }
      return;
    }
    setJustDone(data.rescheduled ? "moved" : "booked");
    setChosen(null);
    await load();
    setPicking(false);
  }

  // ── states ──────────────────────────────────────────────────────────────
  if (loadErr) {
    return (
      <Shell>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1.5 }}>
          We could not open this link
        </Typography>
        <Typography color="text.secondary" sx={{ lineHeight: 1.65 }}>
          {loadErr}
        </Typography>
      </Shell>
    );
  }
  if (!state) {
    return (
      <Shell>
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      </Shell>
    );
  }

  const first = state.student_first;
  const b = state.booking;

  return (
    <Shell>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
        {first}'s 1-on-1 with Dr. Li
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
        20 minutes on Zoom, for {first} and one parent or guardian. Times are
        Pacific.
      </Typography>

      {justDone && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {justDone === "booked"
            ? "You are booked."
            : "Your time has been changed."}{" "}
          A calendar invite is on its way by email.
        </Alert>
      )}

      {/* ── current booking ─────────────────────────────────────────────── */}
      {b && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 700, letterSpacing: 0.4 }}
          >
            BOOKED
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 18, mt: 0.5 }}>
            {dayFmt.format(new Date(b.starts_at))} at{" "}
            {timeFmt.format(new Date(b.starts_at))} Pacific
          </Typography>
          {localHint(b.starts_at) && (
            <Typography variant="body2" color="text.secondary">
              ({localHint(b.starts_at)})
            </Typography>
          )}
          {b.location && (
            <Typography variant="body2" sx={{ mt: 1.5, lineHeight: 1.6 }}>
              {b.location}
            </Typography>
          )}
          {b.can_change ? (
            !picking && (
              <Button
                sx={{ mt: 2 }}
                variant="outlined"
                onClick={() => {
                  setPicking(true);
                  setJustDone(null);
                }}
              >
                Change time
              </Button>
            )
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This meeting has already started, so it can no longer be changed.
              Reply to your confirmation email if you need help.
            </Typography>
          )}
        </Paper>
      )}

      {/* ── picker ──────────────────────────────────────────────────────── */}
      {picking && (
        <>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>
            {b ? "Choose a new time" : "Choose a time"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Times less than 24 hours away are not shown.
          </Typography>

          {byDay.length === 0 ? (
            <Alert severity="info">
              There are no open times left. Please reply to your email and we
              will find a time with you.
            </Alert>
          ) : (
            byDay.map((g) => (
              <Box key={g.day} sx={{ mb: 2.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                  {g.day}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {g.slots.map((s) => (
                    <Button
                      key={s.slot_id}
                      size="small"
                      variant={chosen === s.slot_id ? "contained" : "outlined"}
                      onClick={() => {
                        setChosen(s.slot_id);
                        setSaveErr(null);
                      }}
                      sx={{ minWidth: 84 }}
                    >
                      {timeFmt.format(new Date(s.starts_at))}
                    </Button>
                  ))}
                </Box>
              </Box>
            ))
          )}

          {saveErr && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {saveErr}
            </Alert>
          )}

          {byDay.length > 0 && (
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="contained"
                disabled={!chosen || saving}
                onClick={confirm}
              >
                {saving
                  ? "Saving…"
                  : b
                    ? "Change to this time"
                    : "Book this time"}
              </Button>
              {b && (
                <Button
                  onClick={() => {
                    setPicking(false);
                    setChosen(null);
                    setSaveErr(null);
                  }}
                  disabled={saving}
                >
                  Keep my current time
                </Button>
              )}
              {chosen && (
                <Typography variant="body2" color="text.secondary">
                  {(() => {
                    const s = state.slots.find((x) => x.slot_id === chosen);
                    if (!s) return null;
                    const hint = localHint(s.starts_at);
                    return `${dayFmt.format(new Date(s.starts_at))}, ${timeFmt.format(new Date(s.starts_at))} Pacific${hint ? ` (${hint})` : ""}`;
                  })()}
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F9F7F4",
        display: "flex",
        justifyContent: "center",
        px: 2,
        py: { xs: 4, sm: 8 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 560 }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 20,
              color: "#0F2B4C",
              lineHeight: 1,
            }}
          >
            Ardent
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: 2,
              color: "#C8973A",
              fontWeight: 700,
            }}
          >
            RESEARCH
          </Typography>
        </Box>
        <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3 }} elevation={0}>
          {children}
        </Paper>
      </Box>
    </Box>
  );
}
